/**
 * Mobile card representation of a single recurring template.
 *
 * Replaces the desktop RecurringTable row on phones. Stacks the
 * template's information vertically for thumb reach and small
 * screens. Shares the same data (RecurringTemplate) and the same
 * mutation callbacks as RecurringRow; the parent passes identical
 * props and switches on useBreakpoint.
 *
 * Card layout, top to bottom:
 *   - Head: Template name + Status toggle
 *   - Next field: date + relative (amber if soon)
 *   - Amount + Interval field
 *   - Supplier/Customer field (if applicable)
 *   - Footer: Mode chip + three-dot menu
 */

import { useRef, type CSSProperties } from "react";
import type { RecurringTemplate } from "../recurringTypes";
import {
  formatAmount,
  formatDateLong,
  formatDateRelative,
  formatIntervalLabel,
  getPartyLabel,
  getPartyColumnLabel,
  isDateSoon,
} from "../recurringUtils";
import { MODE_LABELS } from "../recurringMockData";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
  amber: "#B45309",
};

const SOON_DAYS = 14;

interface RecurringCardProps {
  template: RecurringTemplate;
  onTogglePause: () => void;
  onOpenMenu: (anchorEl: HTMLElement) => void;
}

export function RecurringCard({
  template,
  onTogglePause,
  onOpenMenu,
}: RecurringCardProps) {
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const paused = template.status === "paused";
  const ended = template.status === "ended";

  const cardStyle: CSSProperties = {
    background: "#ffffff",
    border: `1px solid ${PALETTE.line}`,
    borderRadius: 10,
    padding: 14,
    margin: "10px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    opacity: paused || ended ? 0.6 : 1,
  };

  const handleMenuClick = () => {
    if (menuBtnRef.current) onOpenMenu(menuBtnRef.current);
  };

  const interval = formatIntervalLabel(template.schedule);
  const nextSoon = isDateSoon(template.nextDate, SOON_DAYS);
  const partyLabel = getPartyLabel(template);
  const partyHeading = getPartyColumnLabel(template.txnType);

  return (
    <div style={cardStyle}>
      <div style={headStyle}>
        <div style={nameStyle}>{template.name}</div>
        <Toggle
          checked={template.status === "active"}
          onChange={onTogglePause}
          ariaLabel={`Pause or resume ${template.name}`}
        />
      </div>

      <Field label="Next">
        {ended || paused ? (
          <div style={{ fontSize: 13, color: PALETTE.ink4, fontWeight: 500 }}>
            {ended ? "Ended" : "Paused"}
          </div>
        ) : (
          <div style={{ fontSize: 13, fontWeight: 600, color: PALETTE.ink }}>
            {formatDateLong(template.nextDate)}
            {template.nextDate && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  fontWeight: nextSoon ? 600 : 500,
                  color: nextSoon ? PALETTE.amber : PALETTE.ink4,
                }}
              >
                · {formatDateRelative(template.nextDate)}
              </span>
            )}
          </div>
        )}
      </Field>

      <Field label="Amount · Interval">
        <div style={{ fontSize: 13 }}>
          <span style={amountStyle}>
            ${formatAmount(template.transaction.amount)}
          </span>
          {" · "}
          <span>
            {interval.primary}
            {interval.secondary && `, ${interval.secondary.toLowerCase()}`}
          </span>
        </div>
      </Field>

      {template.party.type !== "internal" && (
        <Field label={partyHeading}>
          <div style={{ fontSize: 12, color: PALETTE.ink3 }}>{partyLabel}</div>
        </Field>
      )}

      <div style={footerStyle}>
        <span style={modeChipStyle}>{MODE_LABELS[template.mode]}</span>
        <button
          ref={menuBtnRef}
          type="button"
          aria-label={`Actions for ${template.name}`}
          onClick={handleMenuClick}
          style={menuBtnStyle}
        >
          ⋯
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={fieldStyle}>
      <div style={fieldLabelStyle}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  onChange: () => void;
  ariaLabel: string;
}

function Toggle({ checked, onChange, ariaLabel }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onChange}
      style={toggleStyle}
    >
      <span
        style={{
          position: "absolute",
          inset: 0,
          background: checked ? PALETTE.ink : PALETTE.line,
          borderRadius: 10,
          transition: "background 0.2s",
        }}
      >
        <span
          style={{
            position: "absolute",
            height: 16,
            width: 16,
            left: 2,
            top: 2,
            background: "#ffffff",
            borderRadius: "50%",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
            transition: "transform 0.2s",
            transform: checked ? "translateX(14px)" : "translateX(0)",
          }}
        />
      </span>
    </button>
  );
}

const headStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 8,
};

const nameStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: PALETTE.ink,
  lineHeight: 1.3,
  flex: 1,
};

const fieldStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
};

const fieldLabelStyle: CSSProperties = {
  fontSize: 10,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontWeight: 700,
  color: PALETTE.ink4,
};

const amountStyle: CSSProperties = {
  fontWeight: 700,
  fontFamily: "ui-monospace, Menlo, monospace",
};

const modeChipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 8px",
  fontSize: 11.5,
  fontWeight: 500,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 4,
  color: PALETTE.ink2,
  background: "#ffffff",
};

const footerStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: 8,
  borderTop: `1px solid ${PALETTE.lineSoft}`,
};

const toggleStyle: CSSProperties = {
  position: "relative",
  display: "inline-block",
  width: 34,
  height: 20,
  background: "transparent",
  border: 0,
  padding: 0,
  cursor: "pointer",
  flexShrink: 0,
};

const menuBtnStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.ink3,
  fontSize: 18,
  padding: "4px 8px",
  cursor: "pointer",
  borderRadius: 6,
  lineHeight: 1,
};